import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, ShoppingBag, GraduationCap, ClipboardList, IndianRupee, Loader2, Sparkles, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatCard, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY_ACTIVATE = { display_name: "", title: "", about: "", skills: "", location: "" };

const CONFETTI_CSS = `
@keyframes flc-fall { 0% { transform: translateY(-10vh) rotate(0); opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg); opacity: 0; } }
.flc-piece { position: absolute; top: -5%; border-radius: 2px; animation: flc-fall linear forwards; }
@media (prefers-reduced-motion: reduce) { .flc-piece { animation: none !important; opacity: 0; } }
`;

function WelcomeConfetti({ name, onClose }) {
  const pieces = useState(() => Array.from({ length: 60 }, (_, i) => ({
    left: Math.random() * 100, delay: Math.random() * 1.2, dur: 2.4 + Math.random() * 2,
    size: 6 + Math.random() * 8, color: ["#007A4D", "#2563EB", "#7C3AED", "#F5B700", "#06B6D4", "#DB2777"][i % 6],
  })))[0];
  return (
    <div data-testid="fl-welcome-confetti" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <style>{CONFETTI_CSS}</style>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {pieces.map((p, i) => (
          <span key={i} className="flc-piece" style={{ left: `${p.left}%`, width: p.size, height: p.size * 0.6, background: p.color, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s` }} />
        ))}
      </div>
      <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", bounce: 0.45 }}
        onClick={(e) => e.stopPropagation()}
        className="relative rounded-3xl bg-card p-8 sm:p-10 text-center max-w-md mx-4 shadow-2xl border border-border">
        <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB,#7C3AED)" }}>
          <Sparkles className="h-8 w-8 text-white" />
        </div>
        <h2 className="font-display font-extrabold text-3xl tracking-tight mkt-welcome-title">🎉 Congratulations{name ? `, ${name}` : ""}!</h2>
        <p className="text-sm text-muted-foreground mt-3">Your Freelancer application is <b className="text-emerald-600 dark:text-emerald-400">approved</b>. Your dashboard is now active — complete your profile, add payout details and publish your first service!</p>
        <Button data-testid="fl-welcome-start" onClick={onClose} className="mt-6 w-full h-12 rounded-full font-bold gradient-bg text-white border-0 glow-sm">Start Selling</Button>
      </motion.div>
    </div>
  );
}

export default function FreelancerDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [form, setForm] = useState(EMPTY_ACTIVATE);
  const [editOpen, setEditOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/mkt/freelancer/me").then((r) => {
    setMe(r.data);
    if (r.data.active && user) {
      const key = `mkt_welcomed_${user.id || user._id}`;
      if (!localStorage.getItem(key)) { setShowWelcome(true); localStorage.setItem(key, "1"); }
    }
    const f = r.data.freelancer || {};
    setForm({ display_name: f.display_name || "", title: f.title || "", about: f.about || "", location: f.location || "",
      skills: (f.skills || []).join(", "), portfolio: ((r.data.application || {}).portfolio || []).join("\n"), experience: (r.data.application || {}).experience || "" });
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put("/mkt/freelancer/profile", {
        display_name: form.display_name, title: form.title, about: form.about, location: form.location,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        portfolio: (form.portfolio || "").split("\n").map((s) => s.trim()).filter(Boolean),
        experience: form.experience || "",
      });
      toast.success("Profile updated"); setEditOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const subscribe = async () => {
    if (!ref.trim()) { toast.error("Enter payment reference"); return; }
    setBusy(true);
    try { await api.post("/mkt/freelancer/subscribe", { reference: ref.trim() }); toast.success("Submitted — admin will verify shortly"); setSubOpen(false); setRef(""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!me) return <LoadingRows rows={4} />;
  const f = me.freelancer || {};
  const subActive = me.subscription_until && new Date(me.subscription_until) > new Date();

  // ---------- APPLICATION / STATUS VIEW ----------
  if (!me.active) {
    const st = me.application_status;
    const card = {
      none: { icon: Sparkles, tint: "text-primary", title: "Become a Freelancer", body: "Complete your freelancer application, pay the membership, and start selling after approval.", cta: "Start Application" },
      draft: { icon: Sparkles, tint: "text-primary", title: "Finish your application", body: "Your application is saved as a draft — complete it and submit payment to proceed.", cta: "Continue Application" },
      info_requested: { icon: Sparkles, tint: "text-blue-500", title: "More information requested", body: me.info_note || "Admin requested additional information — update your application.", cta: "Update Application" },
      rejected: { icon: Sparkles, tint: "text-red-500", title: "Application rejected", body: me.rejection_reason || "Your application was rejected.", cta: "Fix & Resubmit" },
    }[st] || null;
    return (
      <div data-testid="freelancer-activate-page">
        <PageHeader title="Freelancer Studio" subtitle="Sell services, digital products and courses on the MILTROO Marketplace" testid="freelancer-title" />
        {st === "pending" ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto mkt-status-card bg-card border border-border rounded-3xl p-8 text-center space-y-4" data-testid="fl-pending-card">
            <div className="mx-auto h-14 w-14 rounded-full bg-amber-500/10 flex items-center justify-center"><Loader2 className="h-7 w-7 text-amber-500" /></div>
            <h2 className="font-display font-extrabold text-2xl">Verification in Progress</h2>
            <div className="text-sm space-y-1.5">
              <p className="flex justify-between max-w-xs mx-auto"><span className="text-muted-foreground">Payment Verification</span><b className="text-amber-600">PENDING</b></p>
              <p className="flex justify-between max-w-xs mx-auto"><span className="text-muted-foreground">Application</span><b className="text-amber-600">PENDING APPROVAL</b></p>
            </div>
            <p className="text-xs text-muted-foreground">You'll be notified once the admin verifies your payment and approves your account.</p>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-xl mx-auto rounded-3xl p-[1.5px]" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB,#7C3AED)" }}>
            <div className="rounded-3xl bg-card p-8 text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
                {card && <card.icon className={`h-7 w-7 text-white`} />}
              </div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight">{card?.title}</h2>
              <p className="text-sm text-muted-foreground">{card?.body}</p>
              <p className="text-sm font-bold">Membership: {fmtINR(me.subscription_price)}/month</p>
              {me.role === "PARTNER" && <p className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold inline-block">Existing Partner — same account, no duplicate signup</p>}
              <Link to="/freelancer/apply" data-testid="fl-apply-cta" className="block">
                <Button className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0 glow-sm">{card?.cta}</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  // ---------- ACTIVE DASHBOARD ----------
  const w = me.wallet || {};
  const st = me.stats || {};
  return (
    <div data-testid="freelancer-dashboard">
      {showWelcome && <WelcomeConfetti name={f.display_name || user?.name} onClose={() => setShowWelcome(false)} />}
      <PageHeader title="Freelancer Studio" subtitle={`marketplace/f/${f.slug}`} testid="freelancer-title">
        <Link to={`/marketplace/f/${f.slug}`} data-testid="fl-view-public"><Button variant="outline" className="rounded-full font-bold">View Public Profile</Button></Link>
        <Button data-testid="fl-edit-profile" onClick={() => setEditOpen(true)} className="rounded-full font-bold gradient-bg text-white border-0">Edit Profile</Button>
      </PageHeader>

      {/* subscription banner */}
      {!subActive && (
        <div data-testid="fl-subscription-banner" className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 sm:p-5 flex flex-wrap items-center gap-4">
          <BadgeCheck className="h-6 w-6 text-amber-500 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <p className="font-bold text-sm">{me.subscription_status === "EXPIRED" ? "Membership expired — renew to keep selling" : "Seller subscription required to publish"}</p>
            <p className="text-xs text-muted-foreground">{me.subscription_status === "PENDING_VERIFICATION" ? "Your renewal payment is being verified." : `${fmtINR(me.subscription_price)}/month · publishing & new orders are paused until active. Your listings, orders and earnings stay safe.`}</p>
          </div>
          <Button data-testid="fl-subscribe-btn" onClick={() => setSubOpen(true)} disabled={me.subscription_status === "PENDING_VERIFICATION"} className="rounded-full font-bold gradient-bg text-white border-0">
            {me.subscription_status === "EXPIRED" ? `Renew · ${fmtINR(me.subscription_price)}/mo` : `Subscribe · ${fmtINR(me.subscription_price)}/mo`}
          </Button>
        </div>
      )}
      {subActive && (
        <p data-testid="fl-subscription-active" className="mb-6 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
          Seller plan active until {fmtDate(me.subscription_until)}
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Briefcase} label="Services" value={st.services || 0} testid="fl-stat-services" tint="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={ShoppingBag} label="Products" value={st.products || 0} testid="fl-stat-products" delay={0.05} tint="bg-blue-500/10 text-blue-500" />
        <StatCard icon={GraduationCap} label="Courses" value={st.courses || 0} testid="fl-stat-courses" delay={0.1} tint="bg-violet-500/10 text-violet-500" />
        <StatCard icon={ClipboardList} label="Active Orders" value={st.active_orders || 0} testid="fl-stat-orders" delay={0.15} tint="bg-amber-500/10 text-amber-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          ["Total Earnings", w.total_earnings], ["Pending Clearance", w.pending], ["Available to Withdraw", w.available], ["Withdrawn", w.withdrawn],
        ].map(([label, val], i) => (
          <motion.div key={label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`fl-wallet-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded-2xl p-5 text-white relative overflow-hidden"
            style={{ background: ["linear-gradient(135deg,#007A4D,#059669)", "linear-gradient(135deg,#d97706,#f59e0b)", "linear-gradient(135deg,#2563EB,#06B6D4)", "linear-gradient(135deg,#7C3AED,#DB2777)"][i] }}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/75">{label}</p>
            <p className="font-display font-extrabold text-2xl mt-1.5">{fmtINR(val || 0)}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { to: "/freelancer/listings", icon: Briefcase, t: "Manage Listings", d: "Create and publish services, products & courses", testid: "fl-goto-listings" },
          { to: "/freelancer/orders", icon: ClipboardList, t: "Orders & Delivery", d: "Deliver work and track order status", testid: "fl-goto-orders" },
          { to: "/freelancer/wallet", icon: IndianRupee, t: "Wallet & Payouts", d: "Withdraw earnings to your bank / UPI", testid: "fl-goto-wallet" },
        ].map((c, i) => (
          <motion.div key={c.to} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Link to={c.to} data-testid={c.testid} className="block bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
                <c.icon className="h-5 w-5 text-white" />
              </div>
              <p className="font-display font-bold">{c.t}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.d}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* EDIT PROFILE DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="fl-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Freelancer Profile</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Display name</Label><Input data-testid="fl-edit-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Title</Label><Input data-testid="fl-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Skills (comma separated)</Label><Input data-testid="fl-edit-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Location</Label><Input data-testid="fl-edit-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>About</Label><Textarea data-testid="fl-edit-about" value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} rows={3} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Experience</Label><Input data-testid="fl-edit-experience" value={form.experience || ""} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="e.g. 5 years in web design" /></div>
              <div className="space-y-1.5"><Label>Portfolio links (one per line)</Label><Textarea data-testid="fl-edit-portfolio" value={form.portfolio || ""} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} rows={2} placeholder="https://…" /></div>
            </div>
            <Button data-testid="fl-edit-save" onClick={saveProfile} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SUBSCRIBE DIALOG */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent data-testid="fl-subscribe-dialog">
          <DialogHeader><DialogTitle className="font-display">Seller Subscription · {fmtINR(me.subscription_price)}/month</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {me.payment_link
              ? <a href={me.payment_link} target="_blank" rel="noreferrer" data-testid="fl-sub-pay-link"
                  className="block text-center h-11 leading-[2.75rem] rounded-full font-bold text-white text-sm gradient-bg glow-sm">Pay {fmtINR(me.subscription_price)} Now ↗</a>
              : <p className="text-xs px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600">Payment link not configured — contact support.</p>}
            <div className="space-y-1.5">
              <Label>Payment reference / UPI transaction ID</Label>
              <Input data-testid="fl-sub-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 412345678901" />
            </div>
            <Button data-testid="fl-sub-submit" onClick={subscribe} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for Verification"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
